import * as React from 'react'
import { Pulse } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '../core/card'
import { type PeriodValue } from '../core/period-picker'
import {
  TrendChart,
  PerformanceControls,
  type PerformanceTrends,
} from './performance-charts'

/**
 * ActivityTrendWidget — activity volume over time as a grouped vertical bar
 * chart: one bar group per bucket (days / weeks / months, following the chosen
 * period), one bar per series (deals / emails / samples). Hover a bar for its
 * value; the chart fills the card height. The bar-based counterpart to the
 * line-based {@link ValueTrendWidget} and {@link PipelineTrendWidget}.
 *
 *   <ActivityTrendWidget trends={trends} onPeriodApply={reload} />
 */

export interface ActivityTrendWidgetProps {
  trends: PerformanceTrends
  periodLabel?: React.ReactNode
  defaultPeriod?: PeriodValue
  onPeriodApply?: (value: PeriodValue) => void
  /** Wire the ⋯ menu's "Hide widget" action; hidden when omitted. */
  onHide?: () => void
  title?: React.ReactNode
  className?: string
}

export const ActivityTrendWidget = React.forwardRef<
  HTMLDivElement,
  ActivityTrendWidgetProps
>(
  (
    { trends, periodLabel = 'Period', defaultPeriod, onPeriodApply, onHide, title = 'Activity Trend', className },
    ref
  ) => (
    <Card ref={ref} className={cn('flex w-full max-w-2xl flex-col', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Pulse aria-hidden="true" className="size-4 text-fbui-gray-500" />
          {title}
        </CardTitle>
        <CardAction>
          <PerformanceControls
            periodLabel={periodLabel}
            defaultPeriod={defaultPeriod}
            onPeriodApply={onPeriodApply}
            onHide={onHide}
            hideLabel="Activity trend options"
          />
        </CardAction>
      </CardHeader>
      {/* Chart fills the remaining card height, so it shrinks to a set height. */}
      <CardContent className="flex min-h-0 flex-1 flex-col">
        <TrendChart trends={trends} />
      </CardContent>
    </Card>
  )
)
ActivityTrendWidget.displayName = 'ActivityTrendWidget'
